import { useMemo, useState } from 'react';

import type { IWorkerProject } from '@/api/worker';

type SortOrder = 'asc' | 'desc';
type SortField =
  | 'project_title'
  | 'total_test_runs'
  | 'total_tc'
  | 'total_issues'
  | (typeof ALL_STATUSES)[number];

interface WorkerProjectPanelProps {
  data: IWorkerProject[];
  onProjectClick: (project: IWorkerProject) => void;
}

const ALL_STATUSES = [
  'PASSED',
  'FAILED',
  'BLOCKED',
  'UNTESTED',
  'RETEST',
  'NOT_APPLICABLE',
  'IN_PROGRESS',
  'HOLD',
] as const;

const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const getHeaderStatusClass = (status: string) => {
  switch (status.toUpperCase()) {
  case 'PASSED':
    return 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border-emerald-200';
  case 'FAILED':
    return 'bg-rose-100 hover:bg-rose-200 text-rose-800 border-rose-200';
  case 'BLOCKED':
    return 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-200';
  case 'UNTESTED':
    return 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200';
  case 'RETEST':
    return 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-yellow-200';
  case 'NOT_APPLICABLE':
    return 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border-indigo-200';
  case 'IN_PROGRESS':
    return 'bg-sky-100 hover:bg-sky-200 text-sky-800 border-sky-200';
  case 'HOLD':
    return 'bg-pink-100 hover:bg-pink-200 text-pink-800 border-pink-200';
  default:
    return 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200';
  }
};

const getStatusClass = (status: string) => {
  switch (status.toUpperCase()) {
  case 'PASSED':
    return 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100';
  case 'FAILED':
    return 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100';
  case 'BLOCKED':
    return 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100';
  case 'UNTESTED':
    return 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100';
  case 'RETEST':
    return 'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100';
  case 'NOT_APPLICABLE':
    return 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100';
  case 'IN_PROGRESS':
    return 'bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100';
  case 'HOLD':
    return 'bg-pink-50 text-pink-700 border border-pink-200 hover:bg-pink-100';
  default:
    return 'bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100';
  }
};

export default function WorkerProjectPanel({
  data,
  onProjectClick,
}: WorkerProjectPanelProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('total_issues');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (field: SortField) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const filteredAndSortedProjects = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    const filtered = keyword
      ? data.filter(project =>
        project.project_title.toLowerCase().includes(keyword),
      )
      : data;

    return [...filtered].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'project_title') {
        comparison = a.project_title.localeCompare(b.project_title);
      } else if (sortField === 'total_test_runs') {
        comparison = a.total_test_runs - b.total_test_runs;
      } else if (sortField === 'total_tc') {
        const totalA = ALL_STATUSES.reduce(
          (sum, s) => sum + (a.test_runs[s] ?? 0),
          0,
        );
        const totalB = ALL_STATUSES.reduce(
          (sum, s) => sum + (b.test_runs[s] ?? 0),
          0,
        );
        comparison = totalA - totalB;
      } else if (sortField === 'total_issues') {
        comparison = a.total_issues - b.total_issues;
      } else {
        const valA = a.test_runs[sortField] ?? 0;
        const valB = b.test_runs[sortField] ?? 0;
        comparison = valA - valB;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, search, sortField, sortOrder]);

  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedProjects.slice(start, start + itemsPerPage);
  }, [filteredAndSortedProjects, currentPage]);

  const totalPages =
    Math.ceil(filteredAndSortedProjects.length / itemsPerPage) || 1;

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-xs opacity-90">
        {sortOrder === 'desc' ? '▼' : '▲'}
      </span>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white shadow-sm font-sans">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 bg-slate-50/50">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-bold text-slate-800">Rekap Project</h2>
          <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
            {filteredAndSortedProjects.length} Project
          </span>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setCurrentPage(1);
            }}
            placeholder="Cari project..."
            className="w-64 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto bg-white">
        <table className="w-full min-w-max text-sm border-collapse">
          <thead className="sticky top-0 z-[1] bg-slate-50 text-slate-700 border-b border-slate-200 shadow-2xs">
            <tr>
              {/* 1. Nama Project */}
              <th
                onClick={() => handleSort('project_title')}
                className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold tracking-wider border-r border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition"
              >
                PROJECT {renderSortIndicator('project_title')}
              </th>

              {/* 2. Total Issue (Warna Merah/Rose Soft) */}
              <th
                onClick={() => handleSort('total_issues')}
                className="whitespace-nowrap px-4 py-3 text-center text-xs font-bold tracking-wider border-r border-rose-200 bg-rose-100 text-rose-800 hover:bg-rose-200 cursor-pointer transition"
              >
                Total Issue {renderSortIndicator('total_issues')}
              </th>

              {/* 3. Total Test Run */}
              <th
                onClick={() => handleSort('total_test_runs')}
                className="whitespace-nowrap px-4 py-3 text-center text-xs font-bold tracking-wider border-r border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition"
              >
                Test Run {renderSortIndicator('total_test_runs')}
              </th>

              {/* 4. Ke-8 Status Test Run */}
              {ALL_STATUSES.map(status => (
                <th
                  key={status}
                  onClick={() => handleSort(status)}
                  className={`whitespace-nowrap px-3 py-3 text-center text-xs font-bold tracking-wider border-r border-slate-200 cursor-pointer transition ${getHeaderStatusClass(
                    status,
                  )}`}
                >
                  {formatStatus(status)} {renderSortIndicator(status)}
                </th>
              ))}

              {/* 5. Total TC (Slate Soft) */}
              <th
                onClick={() => handleSort('total_tc')}
                className="whitespace-nowrap px-4 py-3 text-center text-xs font-bold tracking-wider bg-slate-200 text-slate-800 hover:bg-slate-300 cursor-pointer transition"
              >
                Total TC {renderSortIndicator('total_tc')}
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 bg-white">
            {paginatedProjects.map(project => {
              const totalTC = ALL_STATUSES.reduce(
                (sum, status) => sum + (project.test_runs[status] ?? 0),
                0,
              );

              return (
                <tr
                  key={project.project_id}
                  className="group transition-colors hover:bg-blue-50"
                >
                  {/* Nama Project */}
                  <td className="px-6 py-2.5 border-r border-slate-100">
                    <button
                      type="button"
                      onClick={() => onProjectClick(project)}
                      className="max-w-[260px] truncate text-left text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                      title={project.project_title}
                    >
                      {project.project_title}
                    </button>
                  </td>

                  {/* Total Issue Data */}
                  <td className="px-4 py-2.5 text-center border-r border-slate-100">
                    {project.total_issues > 0 ? (
                      <button
                        type="button"
                        onClick={() => onProjectClick(project)}
                        title="Klik untuk melihat detail Create Issue"
                        className="inline-flex min-w-9 items-center justify-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-sm font-bold text-rose-700 transition hover:bg-rose-100 shadow-2xs"
                      >
                        {project.total_issues}
                      </button>
                    ) : (
                      <span className="inline-flex min-w-9 justify-center rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm font-bold text-slate-400">
                        0
                      </span>
                    )}
                  </td>

                  {/* Total Test Run */}
                  <td className="px-4 py-2.5 text-center text-sm font-medium text-slate-700 border-r border-slate-100">
                    {project.total_test_runs}
                  </td>

                  {/* Status Badges */}
                  {ALL_STATUSES.map(status => (
                    <td
                      key={status}
                      className="px-3 py-2.5 text-center border-r border-slate-100"
                    >
                      <span
                        className={`inline-flex min-w-9 justify-center rounded-md px-2.5 py-1 text-sm font-bold ${getStatusClass(
                          status,
                        )}`}
                      >
                        {project.test_runs[status] ?? 0}
                      </span>
                    </td>
                  ))}

                  {/* Total TC */}
                  <td className="px-4 py-2.5 text-center text-sm font-bold text-slate-700">
                    {totalTC}
                  </td>
                </tr>
              );
            })}

            {paginatedProjects.length === 0 && (
              <tr>
                <td
                  colSpan={ALL_STATUSES.length + 4}
                  className="px-6 py-12 text-center text-sm text-slate-500 bg-white"
                >
                  {search
                    ? 'Project tidak ditemukan.'
                    : 'Tidak ada data project pada periode ini.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex shrink-0 items-center justify-between border-t border-slate-200 px-6 py-3 bg-slate-50 text-xs text-slate-600">
        <div>
          Menampilkan{' '}
          <span className="font-semibold">
            {filteredAndSortedProjects.length > 0
              ? (currentPage - 1) * itemsPerPage + 1
              : 0}
          </span>{' '}
          sampai{' '}
          <span className="font-semibold">
            {Math.min(
              currentPage * itemsPerPage,
              filteredAndSortedProjects.length,
            )}
          </span>{' '}
          dari{' '}
          <span className="font-semibold">
            {filteredAndSortedProjects.length}
          </span>{' '}
          data
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 shadow-2xs transition"
          >
            Previous
          </button>

          <span className="px-2 font-medium">
            Hal {currentPage} dari {totalPages}
          </span>

          <button
            type="button"
            onClick={() =>
              setCurrentPage(prev => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || totalPages === 0}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 shadow-2xs transition"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
